/**
 * Der Zugang zum Austauschdienst — die Seite, die im Browser läuft.
 *
 * **Er bildet den Server nach, den es noch nicht gibt.** Portal und
 * Monteur-App laufen auf verschiedenen Herkünften und teilen deshalb keinen
 * `localStorage`; was der Monteur übermittelt, erreichte das Portal nie.
 * Dieser Zugang spricht mit dem Plugin in `apps/portal/vite-austausch.ts`, und
 * damit läuft der Weg dorthin, wo er im Kundenstack auch läuft: über einen
 * Dienst, nicht über den Browserspeicher.
 *
 * **Ein nicht erreichbarer Dienst ist kein Fehler.** Für die Monteur-App ist
 * das der Normalfall — `07` §7.9 baut die ganze Anwendung darauf, dass im
 * Funkloch weitergearbeitet wird. Jede Funktion hier gibt deshalb `undefined`
 * zurück statt zu werfen, und der Aufrufer arbeitet mit seinem lokalen Stand
 * weiter. Ein Austauschdienst, dessen Ausfall die App anhält, hätte genau die
 * Abhängigkeit geschaffen, die das Produkt vermeiden will.
 *
 * Reines TypeScript mit `fetch` — kein Node, kein React. `fetch` gehört zur
 * Browserumgebung und seit Node 18 auch zu Node; die Import-Grenze aus ADR-016
 * nennt Node-**spezifische** APIs, und das ist keine.
 */

/** Der Stand, den beide Seiten teilen. */
export interface Austauschstand {
  readonly einsatzzustand: Readonly<Record<string, string>>;
  readonly bericht: Readonly<Record<string, unknown>>;
  readonly eingang: Readonly<Record<string, unknown>>;
  /** Was der Monteur erfasst hat — Zeiten, Material, Fotos, Notizen. */
  readonly erfassung: Readonly<Record<string, unknown>>;
  readonly neueAuftraege: readonly unknown[];
  readonly widerrufen: readonly string[];
  readonly audit: readonly unknown[];
  readonly fassung: number;
}

export const LEERER_AUSTAUSCH: Austauschstand = {
  einsatzzustand: {},
  bericht: {},
  eingang: {},
  erfassung: {},
  neueAuftraege: [],
  widerrufen: [],
  audit: [],
  fassung: 0,
};

/**
 * Wo der Dienst liegt.
 *
 * Fest auf dem Portal-Port, weil das Plugin dort läuft: Das Portal ist die
 * führende Oberfläche, und eine Monteur-App ohne Portal ist im Demostand kein
 * sinnvoller Zustand. Überschreibbar, weil `pnpm demo` bei belegtem Port einen
 * anderen bekommt und der Ort dann nicht mehr stimmt.
 */
const VORGABE = "http://localhost:4300/demo-austausch";

let adresse = VORGABE;

/** Setzt die Adresse des Dienstes — etwa aus einem Suchparameter. */
export function setzeAustauschadresse(neu: string): void {
  adresse = neu;
}

export function austauschadresse(): string {
  return adresse;
}

/**
 * Holt den geteilten Stand.
 *
 * `undefined` heißt „nicht erreichbar" und ist **kein Fehlerfall**, sondern
 * die zweithäufigste Antwort. Der Aufrufer unterscheidet sie von einem leeren
 * Stand: Leer heißt „nichts passiert", nicht erreichbar heißt „ich weiß es
 * nicht" — und `07` §7.9 verlangt, dass die App diesen Unterschied anzeigt.
 */
export async function holeStand(): Promise<Austauschstand | undefined> {
  try {
    const antwort = await fetch(adresse, { method: "GET" });
    if (!antwort.ok) return undefined;
    return (await antwort.json()) as Austauschstand;
  } catch {
    return undefined;
  }
}

/** Meldet eine Änderung und bekommt den zusammengeführten Stand zurück. */
export async function meldeStand(
  teil: Partial<Austauschstand>,
): Promise<Austauschstand | undefined> {
  try {
    const antwort = await fetch(adresse, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(teil),
    });
    if (!antwort.ok) return undefined;
    return (await antwort.json()) as Austauschstand;
  } catch {
    return undefined;
  }
}

/** Setzt den geteilten Stand zurück. */
export async function leereStand(): Promise<boolean> {
  try {
    const antwort = await fetch(adresse, { method: "DELETE" });
    return antwort.ok;
  } catch {
    return false;
  }
}
